import React, { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { FUNDING_LENDER_STATUSES, FUNDING_LENDER_TYPES } from '../../utils/funding/constants';

const inputClass = 'w-full rounded-xl border border-[var(--fg-line)] bg-white px-3 py-2 text-sm dark:bg-slate-950/20';

const FundingLenderForm = ({ lender, onSubmit, onCancel, saving }) => {
  const [form, setForm] = useState({
    name: '',
    type: 'Bank',
    website: '',
    contact_email: '',
    contact_phone: '',
    description: '',
    min_credit_score: '',
    min_monthly_revenue: '',
    min_time_in_business_months: '',
    min_requested_amount: '',
    max_requested_amount: '',
    funding_speed_days_min: '',
    funding_speed_days_max: '',
    preferred_document_status: '',
    states_supported: 'ALL',
    notes: '',
    internal_notes: '',
    is_featured: false,
    is_active: true,
    status: 'Active',
    accepts_startups: false,
    accepts_low_credit: false,
    commission_available: false,
  });

  useEffect(() => {
    if (!lender) return;
    setForm({
      name: lender.name || '',
      type: lender.type || 'Bank',
      website: lender.website || '',
      contact_email: lender.contactEmail || '',
      contact_phone: lender.contactPhone || '',
      description: lender.description || '',
      min_credit_score: lender.minCreditScore ?? '',
      min_monthly_revenue: lender.minMonthlyRevenue ?? '',
      min_time_in_business_months: lender.minTimeInBusinessMonths ?? '',
      min_requested_amount: lender.minRequestedAmount ?? '',
      max_requested_amount: lender.maxRequestedAmount ?? '',
      funding_speed_days_min: lender.fundingSpeedDaysMin ?? '',
      funding_speed_days_max: lender.fundingSpeedDaysMax ?? '',
      preferred_document_status: lender.preferredDocumentStatus || '',
      states_supported: (lender.statesSupported || ['ALL']).join(', '),
      notes: lender.notes || '',
      internal_notes: lender.internalNotes || '',
      is_featured: Boolean(lender.isFeatured),
      is_active: lender.isActive !== false,
      status: lender.status || 'Active',
      accepts_startups: Boolean(lender.acceptsStartups),
      accepts_low_credit: Boolean(lender.acceptsLowCredit),
      commission_available: Boolean(lender.commissionAvailable),
    });
  }, [lender]);

  const handleChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit({
      name: form.name.trim(),
      type: form.type,
      website: form.website || null,
      contact_email: form.contact_email || null,
      contact_phone: form.contact_phone || null,
      description: form.description || null,
      min_credit_score: form.min_credit_score ? Number(form.min_credit_score) : null,
      min_monthly_revenue: form.min_monthly_revenue ? Number(form.min_monthly_revenue) : null,
      min_time_in_business_months: form.min_time_in_business_months ? Number(form.min_time_in_business_months) : null,
      min_requested_amount: form.min_requested_amount ? Number(form.min_requested_amount) : null,
      max_requested_amount: form.max_requested_amount ? Number(form.max_requested_amount) : null,
      funding_speed_days_min: form.funding_speed_days_min ? Number(form.funding_speed_days_min) : null,
      funding_speed_days_max: form.funding_speed_days_max ? Number(form.funding_speed_days_max) : null,
      preferred_document_status: form.preferred_document_status || null,
      states_supported: form.states_supported.split(',').map((s) => s.trim()).filter(Boolean),
      notes: form.notes || null,
      internal_notes: form.internal_notes || null,
      is_featured: form.is_featured,
      is_active: form.is_active,
      status: form.status,
      accepts_startups: form.accepts_startups,
      accepts_low_credit: form.accepts_low_credit,
      commission_available: form.commission_available,
      products: lender?.products?.length
        ? lender.products.map((p) => ({
          id: p.id,
          name: p.name,
          product_type: p.productType,
          status: p.status || 'Active',
        }))
        : [],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-3 md:grid-cols-2">
        <input className={inputClass} value={form.name} onChange={(e) => handleChange('name', e.target.value)} placeholder="Lender name" required />
        <select className={inputClass} value={form.type} onChange={(e) => handleChange('type', e.target.value)}>
          {FUNDING_LENDER_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
        <input className={inputClass} value={form.website} onChange={(e) => handleChange('website', e.target.value)} placeholder="Website" />
        <input className={inputClass} value={form.contact_email} onChange={(e) => handleChange('contact_email', e.target.value)} placeholder="Contact email" />
        <input className={inputClass} value={form.contact_phone} onChange={(e) => handleChange('contact_phone', e.target.value)} placeholder="Contact phone" />
        <select className={inputClass} value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
          {FUNDING_LENDER_STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>
      </div>
      <textarea className={inputClass} rows={3} value={form.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="Description" />
      <div className="grid gap-3 md:grid-cols-3">
        <input className={inputClass} type="number" value={form.min_credit_score} onChange={(e) => handleChange('min_credit_score', e.target.value)} placeholder="Min credit score" />
        <input className={inputClass} type="number" value={form.min_monthly_revenue} onChange={(e) => handleChange('min_monthly_revenue', e.target.value)} placeholder="Min monthly revenue" />
        <input className={inputClass} type="number" value={form.min_time_in_business_months} onChange={(e) => handleChange('min_time_in_business_months', e.target.value)} placeholder="Min months in business" />
        <input className={inputClass} type="number" value={form.min_requested_amount} onChange={(e) => handleChange('min_requested_amount', e.target.value)} placeholder="Min requested amount" />
        <input className={inputClass} type="number" value={form.max_requested_amount} onChange={(e) => handleChange('max_requested_amount', e.target.value)} placeholder="Max requested amount" />
        <input className={inputClass} value={form.states_supported} onChange={(e) => handleChange('states_supported', e.target.value)} placeholder="States supported (comma-separated)" />
        <input className={inputClass} type="number" value={form.funding_speed_days_min} onChange={(e) => handleChange('funding_speed_days_min', e.target.value)} placeholder="Estimated speed min (days)" />
        <input className={inputClass} type="number" value={form.funding_speed_days_max} onChange={(e) => handleChange('funding_speed_days_max', e.target.value)} placeholder="Estimated speed max (days)" />
        <input className={inputClass} value={form.preferred_document_status} onChange={(e) => handleChange('preferred_document_status', e.target.value)} placeholder="Preferred document status" />
      </div>
      <textarea className={inputClass} rows={2} value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} placeholder="Notes" />
      <textarea className={inputClass} rows={2} value={form.internal_notes} onChange={(e) => handleChange('internal_notes', e.target.value)} placeholder="Internal notes (platform only)" />
      <div className="flex flex-wrap gap-4 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_featured} onChange={(e) => handleChange('is_featured', e.target.checked)} />Featured</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={(e) => handleChange('is_active', e.target.checked)} />Active for matching</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.accepts_startups} onChange={(e) => handleChange('accepts_startups', e.target.checked)} />Accepts startups</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.accepts_low_credit} onChange={(e) => handleChange('accepts_low_credit', e.target.checked)} />Accepts low credit</label>
        <label className="flex items-center gap-2"><input type="checkbox" checked={form.commission_available} onChange={(e) => handleChange('commission_available', e.target.checked)} />Commission available</label>
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={saving}>{saving ? 'Saving...' : lender ? 'Save lender' : 'Create lender'}</Button>
        {onCancel ? <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button> : null}
      </div>
    </form>
  );
};

export default FundingLenderForm;
